// Customer Stores API client for the marketplace
// customer embed — My Account C1, task T7b, design.md AC7.
//
// Wire shape mirrors the Go model exactly (project rule §39):
//   model: internal/marketplace/model/store.go (CustomerStore, StorePatch)
//   handler: internal/marketplace/handler/customer_handler_store.go
//     - HandleStoreList        (GET    /api/v1/marketplace/me/stores)
//     - HandleStoreCreate      (POST   /api/v1/marketplace/me/stores)
//     - HandleStoreUpdate      (PATCH  /api/v1/marketplace/me/stores/{id})
//     - HandleStoreDelete      (DELETE /api/v1/marketplace/me/stores/{id})
//   (The product-store association routes are no longer called from the embed:
//   TD-671 Task 9 reads store assignments through GET /me/owned-products.)
//
// Every list/mutation handler here emits `{"data": <payload>}` directly — NO
// nested envelope key (unlike address-api.ts's `{ addresses: [...] }`).
// Verified by reading customer_handler_store.go: every handler calls
// `writeJSON(w, status, map[string]interface{}{"data": <value>})` with the
// array/struct itself as the value, never wrapped in a second key. So after
// embedApiFetch's .data unwrap, listStores() returns an
// array directly, and createStore()/updateStore() return the struct directly.
//
// §59 If-Match-only versioning (established by T2/T3, mirrored here exactly
// per this task's brief): PATCH/DELETE /me/stores/{id} take version via the
// `If-Match` HTTP header, NEVER in the request body — requireIfMatch() in
// the Go handler rejects a request with no If-Match header before it even
// reaches the service layer. Every mutator below that touches an existing
// store takes `version` as an explicit parameter and sends it as a header,
// not a body field.
//
// §70 name canonicalisation: TRIM on both create and update, NEVER lowercase
// — uniqueness is case-insensitive server-side (index on lower(name)), but
// display must keep the customer's casing (model/store.go's doc-comment).

import { embedApiFetch } from "../../embed-shared/api-client";
import type { OrderSummary } from "../../embed-shared/types";

/**
 * Customer store as returned by the Go API. Mirrors model.CustomerStore
 * exactly. Only `name` is required server-side (NOT NULL); every address
 * field is nullable (*string, omitempty) per migration 086, `phone` per
 * migration 145 (account-area redesign Task 18).
 */
export interface CustomerStoreWire {
  id: string; // UUID
  customer_id: number;
  name: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  /** Store contact phone (migration 145). Format-light by design — the API
   *  validates presence-of-value only, never its shape, so international
   *  formats pass through untouched. */
  phone?: string;
  version: number;
}

/** Request body for POST /api/v1/marketplace/me/stores. Only `name` is
 *  required; every address field (and phone) is optional. */
export interface CreateStoreRequest {
  name: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  phone?: string;
}

/** Partial-update body for PATCH /api/v1/marketplace/me/stores/{id}. Mirrors
 *  model.StorePatch — every field optional (omitted = unchanged server-side).
 *  TD-597: `phone` alone additionally accepts an explicit `null` (or "") as
 *  "clear to NULL" — the Go decoder tells an absent key from a null; the
 *  other optional fields still cannot be cleared (accepted limitation).
 *  Version is NOT here — it goes in the If-Match header (see updateStore). */
export interface UpdateStoreRequest {
  name?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  phone?: string | null;
}

const STORES_PATH = "/api/v1/marketplace/me/stores";
const ORDERS_PATH = "/api/v1/marketplace/me/orders";

// The store tile's per-store orders fetch (account-area redesign Task 18):
// the newest N orders for ONE store, rendered as compact cards with a
// "View all" escape hatch into the filtered Orders section. Exported so
// tests pin the request shape against the constant, never a re-stringed
// literal (rule 55).
export const STORE_TILE_ORDERS_LIMIT = 3;

/**
 * List all stores for the authenticated customer.
 *
 * GET /api/v1/marketplace/me/stores
 * Response (after embedApiFetch .data unwrap): CustomerStoreWire[] directly.
 */
export async function listStores(): Promise<CustomerStoreWire[]> {
  const result = await embedApiFetch<CustomerStoreWire[]>(STORES_PATH);
  return result ?? [];
}

/**
 * Create a new store for the authenticated customer.
 *
 * POST /api/v1/marketplace/me/stores
 * Response (after embedApiFetch .data unwrap): CustomerStoreWire (version=1).
 * Throws ApiError(422, "VALIDATION_ERROR") if name is empty, ApiError(409,
 * "STORE_NAME_CONFLICT") if a store with the same name (case-insensitive)
 * already exists.
 *
 * §70: `name` is trimmed here (client-side) before send — the service layer
 * ALSO trims (belt-and-suspenders is fine; trimming twice is idempotent),
 * but trimming client-side keeps the request body honest about what will be
 * persisted, and keeps this function the single place callers rely on for
 * the trim-never-lowercase contract.
 */
export async function createStore(req: CreateStoreRequest): Promise<CustomerStoreWire> {
  return embedApiFetch<CustomerStoreWire>(STORES_PATH, {
    method: "POST",
    body: { ...req, name: req.name.trim() },
  });
}

/**
 * Update an existing store for the authenticated customer.
 *
 * PATCH /api/v1/marketplace/me/stores/{id}
 * Version travels in the `If-Match` header — NEVER the body (§59, this
 * task's non-negotiable convention). Response (after embedApiFetch .data
 * unwrap): CustomerStoreWire with version+1.
 * Throws ApiError(404, "NOT_FOUND") if the store doesn't belong to this
 * customer (uniform — indistinguishable from "doesn't exist"),
 * ApiError(409, "VERSION_MISMATCH") on a stale version, ApiError(409,
 * "STORE_NAME_CONFLICT") on a name collision.
 */
export async function updateStore(
  id: string,
  version: number,
  patch: UpdateStoreRequest,
): Promise<CustomerStoreWire> {
  const body: UpdateStoreRequest = { ...patch };
  if (body.name != null) body.name = body.name.trim();
  return embedApiFetch<CustomerStoreWire>(`${STORES_PATH}/${id}`, {
    method: "PATCH",
    body,
    headers: { "If-Match": String(version) },
  });
}

/**
 * List the customer's newest orders that carry at least one line assigned to
 * ONE of their own stores — the store tile's orders block (account-area
 * redesign Task 18; ruling S4-A).
 *
 * GET /api/v1/marketplace/me/orders?store_id={uuid}&limit=N
 *
 * The server scopes the list to the caller (placed_by) AND to the store
 * (≥1 order line with that store_id), and it ownership-checks the store
 * itself: a store_id belonging to ANOTHER customer resolves to the same
 * empty envelope as "no orders" — never an error — so this function's empty
 * result is genuinely indistinguishable from "this store has no orders yet".
 * There is deliberately no pagination here (the plain unwrapping variant):
 * the tile asks for exactly STORE_TILE_ORDERS_LIMIT rows and never pages.
 */
export async function listStoreOrders(storeId: string, limit: number = STORE_TILE_ORDERS_LIMIT): Promise<OrderSummary[]> {
  const result = await embedApiFetch<OrderSummary[]>(ORDERS_PATH, {
    query: { limit: String(limit), store_id: storeId },
  });
  return result ?? [];
}

/**
 * Soft-delete a store for the authenticated customer. The server writes the
 * store row only (TD-671 part D step 2): hardware units that were at the
 * store keep their ledger rows and read as Unassigned in the next
 * GET /me/owned-products.
 *
 * DELETE /api/v1/marketplace/me/stores/{id}
 * Version travels in the `If-Match` header (§59). 204 No Content on
 * success; embedApiFetch resolves 204 to `undefined`. Throws ApiError(404)
 * or ApiError(409, "VERSION_MISMATCH") the same way updateStore does.
 */
export async function deleteStore(id: string, version: number): Promise<void> {
  await embedApiFetch<void>(`${STORES_PATH}/${id}`, {
    method: "DELETE",
    headers: { "If-Match": String(version) },
  });
}
